import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PolicyModalityCode } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import type {
    IOneVerifyDemoBlockedMutationRouteMessage,
    IOneVerifyDemoDiscoverPolicyDefinition,
    IOneVerifyDemoGeneralPolicyDefinition,
    IOneVerifyDemoMedicaidPolicyDefinition,
    IOneVerifyDemoPolicyDefinition,
    IOneVerifyDemoPolicyDisplay,
    IOneVerifyDemoSmartScanPolicyDefinition,
} from "@common-shared/types/oneVerifyDemo.types"

export enum OneVerifyDemoPolicyId {
    // Estimate (/estimate)
    generalDavidChen = "generalDavidChen",
    generalLiamMcAllister = "generalLiamMcAllister",
    generalSarahThompson = "generalSarahThompson",
    generalLilyNguyen = "generalLilyNguyen",
    generalRonnieWesley = "generalRonnieWesley",
    generalMemberNotFound = "generalMemberNotFound",
    generalMicahMayor = "generalMicahMayor",

    // Discover / SmartScan
    discoverMiaSullivan = "discoverMiaSullivan",
    smartScanEmmaCarter = "smartScanEmmaCarter",

    // Medicaid
    medicaidMichaelFarmer = "medicaidMichaelFarmer",
    medicaidSuzanneBullard = "medicaidSuzanneBullard",
}

export const COMMON_ONE_VERIFY_DEMO_CONFIG = {
    jwtIssuer: "one-verify-api",
    jwtAudience: "one-verify-demo",

    demoAmplitudeUserPrefix: "demo",

    provisionedDemoEmails: ["demo@meetnirvana.com"],

    jwtExpirationSeconds: 12 * 60 * 60, // 12 hours

    // Non-GET routes allowed for demo sessions.
    // Paths use :practiceId placeholder (normalized at runtime via
    // OneVerifyRouteHelper). Enforced on frontend (HttpHelper) and backend
    // (OneVerifyCognitoAuthenticatedMiddleware).
    allowedMutationRoutes: [
        "/v1/one-verify/practices/:practiceId/coverage/general",
        "/v1/one-verify/practices/:practiceId/coverage/discover",
        "/v1/one-verify/practices/:practiceId/coverage/smart-scan",
        "/v1/one-verify/practices/:practiceId/coverage/medicaid",
        // Validate-only: demo users can review CSV upload results; create remains blocked.
        "/v1/one-verify/practices/:practiceId/network-status/rules/validate",
        "/v1/one-verify/practices/:practiceId/payer-mappings/bulk/validate",
    ],

    // Fallback toast when a blocked demo mutation doesn't match a route prefix below.
    defaultBlockedMutationMessage:
        "This action is disabled in the demo environment.",

    // Feature-specific toasts for blocked demo mutations.
    // First matching prefix wins; keep more specific prefixes above broader ones.
    blockedMutationRouteMessages: [
        {
            prefix: "/v2/one-verify/practices/:practiceId/history/export",
            message:
                "CSV export is available on a full OneVerify account. Contact us to unlock exports for your organization.",
        },
        {
            // Single + bulk recheck both hit .../history/:id/retry
            prefix: "/v2/one-verify/practices/:practiceId/history/",
            message:
                "Re-running checks is available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/overrides",
            message:
                "The Overrides report and coverage override tools are available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/comments",
            message:
                "Policy comments help teams collaborate on coverage follow-up. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/integrations",
            message:
                "Integrations connect to your EHR and billing systems. Sign up to configure integrations for your practice.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/users",
            message:
                "User management is available to practice administrators on a full account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/invites",
            message:
                "User management is available to practice administrators on a full account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/assignments",
            message:
                "Workflow assignment tools help teams route policies. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/network-status",
            message:
                "Saving network status rules is available on a full OneVerify account. You can still upload and review a CSV in demo mode.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/payer-mappings",
            message:
                "Saving payer mappings is available on a full OneVerify account. You can still upload and review a CSV in demo mode.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/configurations",
            message:
                "Changing payer mapping mode is available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/ehr-writes",
            message:
                "Writing coverage results back to your EHR requires a configured integration. Sign up for a full OneVerify account to enable EHR write-back.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/saved-filters",
            message:
                "Saved report filters help your team reuse views across Search and Reports. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/support",
            message:
                "Reporting a coverage issue sends details to our support team for your organization. Sign up for a full account to submit support requests.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/payers",
            message:
                "Managing practice payers and contracted rates is available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/biller-groups",
            message:
                "Biller groups organize users for workflow routing and assignments. Available on a full OneVerify account.",
        },
    ] satisfies IOneVerifyDemoBlockedMutationRouteMessage[],

    defaultModalityCode: PolicyModalityCode.PSY,

    defaultProviderNpi: "1234567890",

    // Session charge in cents.
    defaultSessionCharge: 20000, // $200

    defaultInNetwork: true,

    buildGeneralPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        payerExternalId: string,
        memberId: string,
        dob: string,
        display: IOneVerifyDemoPolicyDisplay,
    ): IOneVerifyDemoGeneralPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.GENERAL,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: this.defaultInNetwork,
                payerExternalId,
                payerName: payerExternalId,
                firstName,
                lastName,
                memberId,
                dob,
            },
        }
    },

    buildDiscoverPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        payerExternalId: string,
        dob: string,
        zipCode: string,
        display: IOneVerifyDemoPolicyDisplay,
    ): IOneVerifyDemoDiscoverPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.DISCOVER,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: this.defaultInNetwork,
                payerExternalId,
                payerName: payerExternalId,
                firstName,
                lastName,
                dob,
                zipCode,
            },
        }
    },

    buildSmartScanPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        dob: string,
        zipCode: string,
        display: IOneVerifyDemoPolicyDisplay,
    ): IOneVerifyDemoSmartScanPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.SMART_SCAN,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: this.defaultInNetwork,
                firstName,
                lastName,
                dob,
                zipCode,
            },
        }
    },

    buildMedicaidPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        state: string,
        dob: string,
        display: IOneVerifyDemoPolicyDisplay,
        memberId?: string,
    ): IOneVerifyDemoMedicaidPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.MEDICAID,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                firstName,
                lastName,
                state,
                dob,
                memberId,
            },
        }
    },

    _generalPolicies: undefined as
        | IOneVerifyDemoGeneralPolicyDefinition[]
        | undefined,
    get generalPolicies(): IOneVerifyDemoGeneralPolicyDefinition[] {
        if (!this._generalPolicies) {
            this._generalPolicies = [
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalDavidChen,
                    "David",
                    "Chen",
                    "ORBLS",
                    "YUN12031213",
                    "1978-05-01",
                    {
                        label: "Standard · In-Network · David Chen",
                        shortDescription:
                            "Active in-network member with a complete set of benefits returned",
                        longDescription:
                            "A clean, successful eligibility check. David is active on a PPO and in-network, so the response comes back fully populated: $25 copay, $2,200 deductible ($1,750 remaining), and $5,000 out-of-pocket max ($4,550 remaining).",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalLiamMcAllister,
                    "Liam",
                    "McAllister",
                    "TXBLS",
                    "CSGA7654321",
                    "1980-04-03",
                    {
                        label: "Standard · Out-of-Network · Liam McAllister",
                        shortDescription:
                            "Active member who is out-of-network - triggers the network status alert",
                        longDescription:
                            "Liam's coverage is active and his benefits look billable, but he's out-of-network, so the network status alert fires. Nirvana uses this alert to help catch an out-of-network member so you don't lose a claim.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalSarahThompson,
                    "Sarah",
                    "Thompson",
                    "BC001",
                    "DUJ123456789",
                    "1985-03-14",
                    {
                        label: "Standard · Full Benefits · Sarah Thompson",
                        shortDescription:
                            "Active member with every response field populated, including a secondary policy",
                        longDescription:
                            "The most detailed response in the demo set. Sarah's plan returns both a copay and coinsurance, deductible and out-of-pocket progress, a separate telehealth benefit structure, and a flag for a second active policy.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalRonnieWesley,
                    "Ronnie",
                    "Wesley",
                    "87726",
                    "00001MHUC",
                    "2007-02-21",
                    {
                        label: "Standard · Partial Recovery · Ronnie Wesley",
                        shortDescription:
                            "Payer confirms coverage but can't provide the copay or coinsurance",
                        longDescription:
                            "A realistic partial-benefits response. The payer confirms Ronnie is active and returns his $5,820 deductible, but can't determine his cost-share, so the response comes back with an Unknown Costshare alert. Nirvana handles incomplete payer data transparently instead of guessing.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalMicahMayor,
                    "Micah",
                    "Mayor",
                    "87726",
                    "839274555",
                    "1990-06-12",
                    {
                        label: "Standard · Plan Year Reset · Micah Mayor",
                        shortDescription:
                            "Active member whose coverage is due to reset soon",
                        longDescription:
                            "OneVerify's Plan Year Reset feature helps you catch members whose coverage is about to change so you can update your records and avoid missed claims.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalMemberNotFound,
                    "David",
                    "Chen",
                    "ORBLS",
                    "00001MHMNF",
                    "1978-05-01",
                    {
                        label: "Standard · Member Not Found · David Chen",
                        shortDescription:
                            "Member not found error - triggers a Policy Not Found alert",
                        longDescription:
                            "Here we'll re-run David's policy with an incorrect member ID to see how Nirvana handles a member not found error. Nirvana surfaces this as a Policy Not Found alert; if you see one, double-check the member ID and other patient details and consider re-running the coverage check.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalLilyNguyen,
                    "Lily",
                    "Nguyen",
                    "HUMANA",
                    "BYP298374652",
                    "1997-06-19",
                    {
                        label: "Standard · Policy Expired · Lily Nguyen",
                        shortDescription:
                            "Member whose coverage has lapsed - re-run with Smart Scan",
                        longDescription:
                            "Lily's policy comes back inactive due to an expired policy. Run this policy then, on the next page, scroll down and submit a Smart Scan check using the recovered ZIP code to see how Nirvana can find her up-to-date coverage.",
                    },
                ),
            ]
        }

        return this._generalPolicies
    },

    _discoverPolicies: undefined as
        | IOneVerifyDemoDiscoverPolicyDefinition[]
        | undefined,
    get discoverPolicies(): IOneVerifyDemoDiscoverPolicyDefinition[] {
        if (!this._discoverPolicies) {
            this._discoverPolicies = [
                this.buildDiscoverPolicy(
                    OneVerifyDemoPolicyId.discoverMiaSullivan,
                    "Mia",
                    "Sullivan",
                    "51062",
                    "1988-10-26",
                    "32803",
                    {
                        label: "Discover · Successful Recovery · Mia Sullivan",
                        shortDescription:
                            "Finds active coverage for Mia using only her name, date of birth, payer, and ZIP",
                        longDescription:
                            "Mia doesn't know her policy number, but Nirvana can find her active coverage using only her name, date of birth, payer, and ZIP. In OneVerify, if a Discover check finds an active policy, it automatically runs a Standard check as well in order to retrieve the patient's full coverage details.",
                    },
                ),
            ]
        }

        return this._discoverPolicies
    },

    _smartScanPolicies: undefined as
        | IOneVerifyDemoSmartScanPolicyDefinition[]
        | undefined,
    get smartScanPolicies(): IOneVerifyDemoSmartScanPolicyDefinition[] {
        if (!this._smartScanPolicies) {
            this._smartScanPolicies = [
                this.buildSmartScanPolicy(
                    OneVerifyDemoPolicyId.smartScanEmmaCarter,
                    "Emma",
                    "Carter",
                    "1992-07-14",
                    "75201",
                    {
                        label: "Smart Scan · Successful Recovery · Emma Carter",
                        shortDescription:
                            "Finds active coverage for Emma using only her name, date of birth, and ZIP",
                        longDescription:
                            "Use Smart Scan when you only have a name, date of birth, and ZIP. Useful when a Standard check can't find active coverage. In OneVerify, if a Smart Scan check finds an active policy, it automatically runs a Standard check as well in order to retrieve the patient's full coverage details.",
                    },
                ),
            ]
        }

        return this._smartScanPolicies
    },

    _medicaidPolicies: undefined as
        | IOneVerifyDemoMedicaidPolicyDefinition[]
        | undefined,
    get medicaidPolicies(): IOneVerifyDemoMedicaidPolicyDefinition[] {
        if (!this._medicaidPolicies) {
            this._medicaidPolicies = [
                this.buildMedicaidPolicy(
                    OneVerifyDemoPolicyId.medicaidMichaelFarmer,
                    "Michael",
                    "Farmer",
                    "FL",
                    "1993-01-29",
                    {
                        label: "Medicaid · Managed Care Plan · Michael Farmer",
                        shortDescription:
                            "Active Florida Medicaid - managed care plan identified",
                        longDescription:
                            'Michael is actively enrolled in Florida Medicaid at $0 cost to the member, and the response names his managed care organization: Sunshine State Health Plan. While most eligibility tools stop at "active Medicaid," Nirvana identifies the MCO to aid in claims filing.',
                    },
                    "01MCOACTIVE",
                ),
                this.buildMedicaidPolicy(
                    OneVerifyDemoPolicyId.medicaidSuzanneBullard,
                    "Suzanne",
                    "Bullard",
                    "NY",
                    "2001-11-13",
                    {
                        label: "Medicaid · State Plan · Suzanne Bullard",
                        shortDescription:
                            "Active New York Medicaid directly through the state - no managed care plan",
                        longDescription:
                            "Suzanne is enrolled in New York Medicaid through the state plan with no MCO. Compare with Michael Farmer to see the difference between managed-care and state-plan Medicaid responses.",
                    },
                    "01STATEACTIVE",
                ),
            ]
        }

        return this._medicaidPolicies
    },

    _policies: undefined as IOneVerifyDemoPolicyDefinition[] | undefined,
    get policies(): IOneVerifyDemoPolicyDefinition[] {
        if (!this._policies) {
            this._policies = [
                ...this.generalPolicies,
                ...this.discoverPolicies,
                ...this.smartScanPolicies,
                ...this.medicaidPolicies,
            ]
        }

        return this._policies
    },
}
